import { createContext, useContext, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CustomScrollbar from '@/components/common/CustomScrollbar';
import { type NavItem, type NavSection } from '@/config/navigation';
import { useRole } from '@/hooks/useRole';
import { activeNavPath, countLeaves, searchNavItems } from './navUtils';
import React from 'react';
import { useNavigation } from '@/hooks/useNavigation';

interface SidebarNavProps {
  onNavigate?: () => void;
  /** Icon-only rail mode — hides all text labels, shows them as hover tooltips instead. */
  collapsed?: boolean;
  /** When non-empty, shows a flat list of matching pages/modules instead of the grouped nav. */
  filter?: string;
}

const ITEM_FONT_SIZE = '0.8125rem'; // 13px
const CHILD_FONT_SIZE = '0.75rem'; // 12px
const GROUP_LABEL_FONT_SIZE = '0.625rem'; // 10px
const ICON_FONT_SIZE = 16;
const ICON_TILE_SIZE = 28;
const ACCENT_BORDER = 3;

/** Rounded-square tile behind a top-level item's icon. */
function iconTileSx(
  sidebar: { hoverBackground: string; activeBackground: string },
  active: boolean,
  collapsed: boolean,
) {
  return {
    minWidth: ICON_TILE_SIZE,
    width: ICON_TILE_SIZE,
    height: ICON_TILE_SIZE,
    mr: collapsed ? 0 : 1.25,
    borderRadius: 1.25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'inherit',
    bgcolor: active ? sidebar.activeBackground : sidebar.hoverBackground,
    '& .MuiSvgIcon-root': { fontSize: ICON_FONT_SIZE },
  } as const;
}

/**
 * `to` values of the currently active nav item and its ancestors, shared down
 * the tree so every level agrees on the single most-specific match instead of
 * each item independently prefix-matching `pathname` against its own `to`.
 */
const ActiveNavContext = createContext<Set<string>>(new Set());
const useActiveNav = () => useContext(ActiveNavContext);

function LeafLink({
  item,
  depth,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  depth: number;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const sidebar = useTheme().tokens.component.sidebar;
  const active = useActiveNav().has(item.to);
  const button = (
    <ListItemButton
      component={NavLink}
      to={item.to}
      end={item.to === '/'}
      selected={active}
      onClick={onNavigate}
      sx={{
        pl: collapsed ? 0 : 1.75 - ACCENT_BORDER / 8,
        pr: collapsed ? 0 : 2,
        py: depth === 0 ? 0.375 : 0.5,
        ml: 1,
        mr: 1,
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: 0.75,
        border: collapsed ? 'none' : `1px solid transparent`,
        color: active ? sidebar.activeText : sidebar.mutedText,
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&.Mui-selected': {
          bgcolor: sidebar.activeBackground,
          borderColor: collapsed ? 'transparent' : theme => theme.palette.primary.light ,
          '&:hover': { bgcolor: sidebar.activeBackground },
        },
        '&:hover': { bgcolor: sidebar.hoverBackground },
      }}
    >
      <ListItemIcon
       sx={{
         minWidth: ICON_TILE_SIZE,
         width: ICON_TILE_SIZE,
         height: ICON_TILE_SIZE,
         mr: collapsed ? 0 : 1.25,
         flexShrink: 0,
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
         borderRadius: '8px',
         bgcolor: active ? sidebar.activeText : sidebar.hoverBackground,
         color: active ? sidebar.activeBackground : sidebar.mutedText,
         transition: 'background-color 120ms ease, color 120ms ease',
         '& .MuiSvgIcon-root': {
           fontSize: item.icon ? ICON_FONT_SIZE : 7,
         },
       }}
      >
        {item.icon ?? <FiberManualRecordIcon />}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          slotProps={{
            primary: {
              noWrap: true,
              color: 'inherit',
              sx: {
                fontSize: depth === 0 ? ITEM_FONT_SIZE : CHILD_FONT_SIZE,
                fontWeight: active ? 600 : 500,
              },
            },
          }}
        />
      )}
    </ListItemButton>
  );
  return collapsed ? (
    <Tooltip title={item.label} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
}

function BranchItem({ item, collapsed, onNavigate, labelOverride, countBadge,}: { item: NavItem; collapsed: boolean; onNavigate?: () => void; labelOverride?: string; countBadge?: number;}) {
  const sidebar = useTheme().tokens.component.sidebar;
  const active = useActiveNav().has(item.to);
  const [open, setOpen] = useState(false);
  const label = labelOverride ?? item.label;

  const handleNavigate = () => {
    if (!collapsed) {
      setOpen((previous) => !previous);
    }
    onNavigate?.();
  };

  const row = (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        mx: 1,
        borderRadius: 0,
        borderLeft: collapsed ? 'none' : `${ACCENT_BORDER}px solid transparent`,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: -ACCENT_BORDER,
          top: '8px',
          bottom: '8px',
          width: `${ACCENT_BORDER}px`,
          bgcolor: sidebar.activeText,
          borderRadius: '4px',
          display: !collapsed && active ? 'block' : 'none',
          pointerEvents: 'none',
        },
        '&:hover': { bgcolor: sidebar.hoverBackground, },
      }}
    >

      <ListItemButton
        component={NavLink}
        to={item.to}
        end={item.to === '/'}
        onClick={handleNavigate}
        sx={{
          flex: 1,
          minWidth: 0,
          py: collapsed ? 0.375 : 0.5,
          pl: collapsed ? 0 : 1.75 - ACCENT_BORDER / 8,
          pr: collapsed ? 0 : 0.5,
          borderRadius: 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: active ? sidebar.activeText : sidebar.mutedText,
          textDecoration: 'none',
          '&:hover': {
            bgcolor: 'transparent',
          },
        }}
      >
        {collapsed ? (
          <ListItemIcon sx={iconTileSx(sidebar, active, true)}>
            {item.icon ?? <FiberManualRecordIcon />}
          </ListItemIcon>
        ) : (
          <React.Fragment>
            <ListItemText
              sx={{ flex: '0 1 auto', minWidth: 0 }}
              primary={label}
              slotProps={{
                primary: {
                  noWrap: true,
                  color: 'inherit',
                  sx: {
                    fontSize: GROUP_LABEL_FONT_SIZE,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                  },
                },
              }}
            />

            <Box sx={{ flex: 1, height: '1px', bgcolor: sidebar.border, mx: 1, }}/>

            {countBadge !== undefined && (
              <Typography component="span" variant="caption" sx={{ fontSize: GROUP_LABEL_FONT_SIZE, fontWeight: 600, color: 'inherit', bgcolor: sidebar.hoverBackground, borderRadius: 999, px: 0.875, lineHeight: 1.6, flexShrink: 0,}} >
                {countBadge}
              </Typography>
            )}
          </React.Fragment>
        )}
      </ListItemButton>

      {!collapsed && (
        <IconButton
          type="button"
          size="small"
          aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
          aria-expanded={open}
          onClick={() => setOpen((previous) => !previous)}
          sx={{
            p: 0.375,
            mr: 0.5,
            color: active ? sidebar.activeText : sidebar.mutedText,
            flexShrink: 0,
          }}
        >
          {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
        </IconButton>
      )}
    </Box>
  );

  return (
    <React.Fragment>
      {collapsed ? (<Tooltip title={label} placement="right">{row}</Tooltip>) : (row)}
      <Collapse in={!collapsed && open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding dense>
          {item.children?.map((child) =>
            child.children?.length ? (
              <BranchItem key={child.to} item={child} collapsed={false} onNavigate={onNavigate} />
            ) : (
              <LeafLink key={child.to} item={child} depth={1} collapsed={false} onNavigate={onNavigate}/>
            ),
          )}
        </List>
      </Collapse>
    </React.Fragment>
  );
}

/** Plain, non-collapsible list of a section's items — used for unlabelled sections and for the icon-only rail. */
function SectionList({
  section,
  collapsed,
  onNavigate,
}: {
  section: NavSection;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    <List dense disablePadding>
      {section.items.map((item) =>
        item.children?.length ? (
          <BranchItem key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
        ) : (
          <LeafLink
            key={item.to}
            item={item}
            depth={0}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ),
      )}
    </List>
  );
}

/** A section whose single item is itself a branch — the branch's own header stands in for the group. */
function PortalGroup({ section, onNavigate }: { section: NavSection; onNavigate?: () => void }) {
  const count = useMemo(() => countLeaves(section.items), [section.items]);
  const [item] = section.items;
  if (!item) return null;
  return (
    <List dense disablePadding>
      <BranchItem
        item={item}
        collapsed={false}
        onNavigate={onNavigate}
        labelOverride={section.label}
        countBadge={count}
      />
    </List>
  );
}

function CollapsibleGroup({
  section,
  onNavigate,
}: {
  section: NavSection;
  onNavigate?: () => void;
}) {
  const sidebar = useTheme().tokens.component.sidebar;
  const activeSet = useActiveNav();
  const count = useMemo(() => countLeaves(section.items), [section.items]);
  const active = useMemo(
    () => section.items.some((item) => activeSet.has(item.to)),
    [section.items, activeSet],
  );
  const [open, setOpen] = useState(active);
  // Same re-open-on-navigation fix as BranchItem — see its comment.
  const [wasActive, setWasActive] = useState(active);
  if (active !== wasActive) {
    setWasActive(active);
    if (active) setOpen(true);
  }

  return (
    <List
      dense
      disablePadding
      subheader={
        <ListItemButton
          onClick={() => setOpen((o) => !o)}
          sx={{
            py: 0.5,
            mx: 1,
            px: 1.75 - ACCENT_BORDER / 8,
          }}
        >
          <ListItemText
            sx={{ flex: '0 1 auto', minWidth: 0 }}
            primary={section.label}
            slotProps={{
              primary: {
                sx: {
                  fontSize: GROUP_LABEL_FONT_SIZE,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: 'inherit',
                },
              },
            }}
          />
          <Box sx={{ flex: 1, height: '1px', bgcolor: sidebar.border, mx: 1 }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: GROUP_LABEL_FONT_SIZE,
              fontWeight: 600,
              color: 'inherit',
              bgcolor: sidebar.hoverBackground,
              borderRadius: 999,
              px: 0.875,
              lineHeight: 1.6,
              mr: 0.5,
            }}
          >
            {count}
          </Typography>
          {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
        </ListItemButton>
      }
    >
      <Collapse in={open} timeout="auto" unmountOnExit>
        {section.items.map((item) =>
          item.children?.length ? (
            <BranchItem key={item.to} item={item} collapsed={false} onNavigate={onNavigate} />
          ) : (
            <LeafLink
              key={item.to}
              item={item}
              depth={0}
              collapsed={false}
              onNavigate={onNavigate}
            />
          ),
        )}
      </Collapse>
    </List>
  );
}

/** Data-driven sidebar navigation. Mounted once by {@link AppShell}. */
export function SidebarNav({ onNavigate, collapsed = false, filter = '' }: SidebarNavProps) {
  const { portal, roleCode } = useRole();
  const { pathname } = useLocation();
  const navigation = useNavigation(portal, roleCode);
  const activeSet = useMemo(() => activeNavPath(navigation, pathname), [navigation, pathname]);
  const query = filter.trim();
  const matches = useMemo(
    () => (query ? searchNavItems(navigation, query) : []),
    [navigation, query],
  );

  if (query) {
    return (
      <ActiveNavContext.Provider value={activeSet}>
        <CustomScrollbar height="100%" noPadding>
          <Box component="nav" aria-label="Search results" sx={{ py: 1 }}>
            {matches.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ px: 2, py: 1, fontSize: ITEM_FONT_SIZE, color: 'text.secondary' }}
              >
                No menus match “{query}”.
              </Typography>
            ) : (
              <List dense>
                {matches.map((item) => (
                  <LeafLink
                    key={item.to}
                    item={item}
                    depth={0}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                ))}
              </List>
            )}
          </Box>
        </CustomScrollbar>
      </ActiveNavContext.Provider>
    );
  }

  return (
    <ActiveNavContext.Provider value={activeSet}>
      <CustomScrollbar height="100%" noPadding>
        <Box component="nav" aria-label="Main navigation" sx={{ py: 1 }}>
          {navigation.map((section) => {
            if (collapsed || !section.label) {
              return (
                <SectionList
                  key={section.id}
                  section={section}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              );
            }
            const isSingleBranch =
              section.items.length === 1 && Boolean(section.items[0]?.children?.length);
            return isSingleBranch ? (
              <PortalGroup key={section.id} section={section} onNavigate={onNavigate} />
            ) : (
              <CollapsibleGroup key={section.id} section={section} onNavigate={onNavigate} />
            );
          })}
        </Box>
      </CustomScrollbar>
    </ActiveNavContext.Provider>
  );
}
